import {
  BehaviorSubject,
  forkJoin,
  Observable,
  of,
  throwError as observableThrowError,
} from 'rxjs';
import { Injectable } from '@angular/core';
import { List } from 'immutable';
import { File } from '../files/file.model';
import { FilesService } from '../files/files.service';
import { LoggerService } from '../shared/services/logger/logger.service';
import { I2gNgTrackingActionBuilder } from 'i2g-ng-tracking';
import {
  ITrackingObject,
  TrackingInputTypeValuesEnum,
} from '../tracking/tracking.data';
import { TrackingDispatcher } from '../tracking/tracking.dispatcher';
import { AttachmentView } from './attachment-view.model';
import { AttachmentBuilder } from './attachment.builder';
import { Attachment } from './attachment.model';
import { AttachmentsService } from './attachments.service';
import { catchError, distinctUntilChanged, filter, map } from 'rxjs/operators';

// TODO: refactor attachments to load blob instead of json
@Injectable()
export class AttachmentsStore {
  private _files: BehaviorSubject<List<AttachmentView>> = new BehaviorSubject<
    List<AttachmentView>
  >(List([]));
  private _inProgress: BehaviorSubject<
    List<AttachmentView>
  > = new BehaviorSubject<List<AttachmentView>>(List([]));

  private _trackingObject: ITrackingObject;

  files$: Observable<List<AttachmentView>> = this._files
    .asObservable()
    .pipe(distinctUntilChanged((a, b) => a && a.equals(b)));
  inProgress$: Observable<
    List<AttachmentView>
  > = this._inProgress
    .asObservable()
    .pipe(distinctUntilChanged((a, b) => a && a.equals(b)));

  constructor(
    private _attachmentsService: AttachmentsService,
    private _loggerService: LoggerService,
    private _filesService: FilesService,
    private _attachmentBuilder: AttachmentBuilder,
    private _trackingDispatcher: TrackingDispatcher,
    private _trackingActionBuilder: I2gNgTrackingActionBuilder
  ) {
    _attachmentsService.onAfterAddingFile$
      .pipe(filter((x) => !!x))
      .subscribe((e) => this._onNewUpload(e));

    _attachmentsService.onProgressItem$
      .pipe(filter((pi) => !!pi))
      .subscribe((e) => this._onProgressUpload(e));

    _attachmentsService.onSuccessItem$
      .pipe(filter((si) => !!si))
      .subscribe((e) => this._onSuccessUpload(e));

    _attachmentsService.onErrorItem$
      .pipe(filter((ei) => !!ei))
      .subscribe((e) => this._onErrorUpload(e));
  }

  // TODO: we should set the tracking object in the constructor
  setTrackingObject(trackingObject: ITrackingObject) {
    this._trackingObject = trackingObject;
  }

  addToQueue(files: Blob[]) {
    this._attachmentsService.addToQueue(files);
  }

  addAttachment(attachment: Attachment, emitEvent: boolean = true) {
    const attachmentView = new AttachmentView({
      [AttachmentView.KEYS.ATTACHMENT]: attachment,
      [AttachmentView.KEYS.EMIT_EVENT]: emitEvent,
    });

    this._filesService
      .getFile(attachmentView.id)
      .pipe(
        // TODO: handle all possible error responses
        catchError((error) => {
          const errorMessage =
            error && error.length === 2
              ? error[1].description
              : 'Unexpected Error';
          const result = attachmentView.setError(errorMessage);

          this._pushNewAttachment(result);

          return observableThrowError(error);
        })
      )
      .subscribe((file: File) =>
        this._pushNewAttachment(attachmentView.setFile(file))
      );
  }

  setAttachments(attachments: List<Attachment>, emitEvent: boolean = true) {
    this._attachmentsService.cancelAll();

    if (!attachments.isEmpty()) {
      const attachmentRequests = attachments
        .map((attachment) => {
          const attView = new AttachmentView({
            [AttachmentView.KEYS.ATTACHMENT]: attachment,
            [AttachmentView.KEYS.EMIT_EVENT]: emitEvent,
          });

          return this._filesService.getFile(attView.id).pipe(
            map((file) => attView.setFile(file)),
            // TODO: handle all possible error responses
            catchError((error) => {
              const errorMessage =
                error && error.length === 2
                  ? error[1].description
                  : 'Unexpected Error';

              // It's not throwing an error here 'cause we need to handle errors separately within attachments list
              return of(attView.setError(errorMessage));
            })
          );
        })
        .toArray();

      forkJoin(attachmentRequests).subscribe((result: any[]) => {
        this._files.next(List(result));
      });
    } else {
      this._files.next(List());
    }
  }

  clearAttachments() {
    this._attachmentsService.cancelAll();

    this._files.next(List([]));
  }

  updateAttachment(attachment: AttachmentView) {
    const entityId = attachment.id;

    this._loggerService.log('editing attachment id: ', entityId, 'from store');

    if (entityId) {
      const currentFiles = this._files.value;
      const index: number = currentFiles.findIndex(
        (att: AttachmentView) => att.id === entityId && !att.equals(attachment)
      );

      if (index > -1) {
        this._files.next(currentFiles.set(index, attachment));
      }
    }
  }

  deleteAttachmentById(attachmentId: string) {
    this._loggerService.log(
      'deleting attachment id: ',
      attachmentId,
      'from store'
    );

    if (attachmentId) {
      const currentFiles = this._files.value;
      const newFiles = currentFiles.filterNot(
        (att: AttachmentView) => att.id === attachmentId
      ) as List<AttachmentView>;

      if (!newFiles.equals(currentFiles)) {
        this._files.next(newFiles);
      }
    }
  }

  cancelUpload(attachment: AttachmentView) {
    const fileItem = attachment.fileItem;

    if (fileItem) {
      this._attachmentsService.cancelUpload(fileItem);
      this._removeUploading(fileItem);
    }
  }

  retryUpload(attachment: AttachmentView) {
    const currentFiles = this._inProgress.value;
    const fileItem = attachment.fileItem;
    const index: number = currentFiles.findIndex((att: AttachmentView) =>
      att.hasFileItem(fileItem)
    );

    if (index > -1) {
      this._inProgress.next(
        currentFiles.update(index, (file) => file.setError(''))
      );
      this._attachmentsService.uploadFile(attachment.fileItem);
    }
  }

  private _onNewUpload(fileItem: any) {
    this._loggerService.log('on new upload', fileItem.file);

    if (fileItem) {
      const currentQueue = this._inProgress.value;
      const index: number = currentQueue.findIndex((e: any) =>
        e.hasFileItem(fileItem)
      );

      if (index < 0) {
        const file = new File({
          [File.KEYS.ID]: null,
          [File.KEYS.URL]: null,
          [File.KEYS.MIME_TYPE]: fileItem.file.type,
          [File.KEYS.FILE_SIZE]: fileItem.file.size,
        });
        const newFile = AttachmentView.fromFileItem(fileItem, file);
        this._inProgress.next(currentQueue.push(newFile));
      }
    }
  }

  private _onProgressUpload(data: any[]) {
    if (data && data.length === 2) {
      const [fileItem, progress] = data;

      const currentFiles = this._inProgress.value;
      const index: number = currentFiles.findIndex((att: AttachmentView) =>
        att.hasFileItem(fileItem)
      );

      if (index > -1) {
        this._inProgress.next(
          currentFiles.update(index, (file) => file.setProgress(progress))
        );
      }
    }
  }

  private _onSuccessUpload(data: any[]) {
    if (data && data.length === 2) {
      const fileItem: any = data[0];
      const response: any = JSON.parse(data[1]);

      this._loggerService.log('on success upload', fileItem.file, response);

      const file = new File({
        [File.KEYS.ID]: response.file.id,
        [File.KEYS.URL]: response.file.url,
        [File.KEYS.MIME_TYPE]: response.file.mime_type,
        [File.KEYS.FILE_SIZE]: response.file.size,
      });

      const attachment: AttachmentView = this._attachmentBuilder.buildAttachmentView(
        file,
        fileItem.file.name
      );

      if (this._trackingObject) {
        const uploadCompleted = this._trackingActionBuilder.buildUploadCompleted(
          attachment.id,
          null,
          TrackingInputTypeValuesEnum.attachment
        );

        this._trackingDispatcher.trackEvent(
          this._trackingObject,
          uploadCompleted
        );
      }

      this._removeUploading(fileItem);
      this._pushNewAttachment(attachment);
    }
  }

  private _onErrorUpload(data: any[]) {
    if (data && data.length === 2) {
      const [fileItem, response] = data;
      this._loggerService.log('on error upload', fileItem.file, response);

      if (this._trackingObject) {
        const uploadFailed = this._trackingActionBuilder.buildUploadFailed(
          null,
          null,
          TrackingInputTypeValuesEnum.attachment
        );

        this._trackingDispatcher.trackEvent(this._trackingObject, uploadFailed);
      }

      const currentFiles = this._inProgress.value;
      const index: number = currentFiles.findIndex((att: AttachmentView) =>
        att.hasFileItem(fileItem)
      );

      if (index > -1) {
        this._inProgress.next(
          currentFiles.update(index, (file) =>
            file
              .setError(response.error ? response.error.description : '') // TODO: handle errors properly
              .setProgress(0)
          )
        );
      }
    }
  }

  private _removeUploading(fileItem: any) {
    this._loggerService.log('remove uploading', fileItem.file);

    if (fileItem) {
      const uploadingFiles = this._inProgress.value;
      const newList = uploadingFiles.filterNot((att: AttachmentView) =>
        att.hasFileItem(fileItem)
      ) as List<AttachmentView>;

      if (!newList.equals(uploadingFiles)) {
        this._inProgress.next(newList);
      }
    }
  }

  private _pushNewAttachment(newAttachment: AttachmentView) {
    if (newAttachment) {
      const currentFiles = this._files.value;
      const index: number = currentFiles.findIndex(
        (attachment: AttachmentView) => attachment.id === newAttachment.id
      );

      if (index < 0) {
        this._files.next(currentFiles.push(newAttachment));
      }
    }
  }
}
